// src/locales/translations/content.ts
import { SupportedLanguage } from '@/config/routes';

export interface CommonTranslations {
  errors: {
    invalidColor: string;
    invalidResolution: string;
    downloadFailed: string;
    fullscreenError: string;
    componentError: string;
    retry: string;
    title: string;
    description: string;
    tryAgain: string;
    backHome: string;
  };
  fullscreen: {
    exitMac: string;
    exitWindows: string;
  };
  resolution: {
    selectPlaceholder: string;
    custom: string;
    width: string;
    height: string;
  };
  aria: {
    customColorInput: string;
    fullscreenButton: string;
    downloadButton: string;
  };
  buttons: {
    fullscreen: string;
    download: string;
    close: string;
  };
  tooltips: {
    enterFullscreen: string;
    exitFullscreen: string;
    downloadImage: string;
  };
  headings: {
    resolution: string;
    customColor: string;
  };
  messages: {
    downloadSuccess: string;
    downloadError: string;
    fullscreenError: string;
    fullscreenEnter: string;
  };
}

export const contentTranslations: Record<SupportedLanguage, CommonTranslations> = {
  en: {
    errors: {
      invalidColor: 'Invalid color code',
      invalidResolution: 'Invalid resolution',
      downloadFailed: 'Failed to download background',
      fullscreenError: 'Failed to enter fullscreen mode',
      componentError: 'Component error occurred',
      retry: 'Retry',
      title: 'Something went wrong',
      description: 'An error occurred while processing your request',
      tryAgain: 'Try Again',
      backHome: 'Back to Home'
    },
    fullscreen: {
      exitMac: "Press ⌘+⌃+F to exit",
      exitWindows: "Press Esc to exit"
    },
    resolution: {
      selectPlaceholder: "Select resolution",
      custom: "Custom",
      width: "Width",
      height: "Height"
    },
    aria: {
      customColorInput: "Enter custom color hex code",
      fullscreenButton: "Toggle fullscreen mode",
      downloadButton: "Download background image"
    },
    buttons: {
      fullscreen: 'Enter Fullscreen',
      download: 'Download Background',
      close: 'Close'
    },
    tooltips: {
      enterFullscreen: 'Click to enter fullscreen mode',
      exitFullscreen: 'Press ESC to exit fullscreen',
      downloadImage: 'Download as PNG image'
    },
    headings: {
      resolution: 'Resolution',
      customColor: 'Custom Color'
    },
    messages: {
      downloadSuccess: 'Background downloaded successfully',
      downloadError: 'Failed to download background',
      fullscreenError: 'Failed to enter fullscreen mode',
      fullscreenEnter: 'Entered fullscreen mode'
    }
  },
  es: {
    errors: {
      invalidColor: 'Código de color inválido',
      invalidResolution: 'Resolución inválida',
      downloadFailed: 'Error al descargar el fondo',
      fullscreenError: 'Error al entrar en modo pantalla completa',
      componentError: 'Se produjo un error en el componente',
      retry: 'Reintentar',
      title: 'Algo salió mal',
      description: 'Se produjo un error al procesar su solicitud',
      tryAgain: 'Intentar de nuevo',
      backHome: 'Volver al inicio'
    },
    fullscreen: {
      exitMac: "Presiona ⌘+⌃+F para salir",
      exitWindows: "Presiona Esc para salir"
    },
    resolution: {
      selectPlaceholder: "Seleccionar resolución",
      custom: "Personalizado",
      width: "Ancho",
      height: "Alto"
    },
    aria: {
      customColorInput: "Ingresa código hex de color personalizado",
      fullscreenButton: "Alternar modo pantalla completa",
      downloadButton: "Descargar imagen de fondo"
    },
    buttons: {
      fullscreen: 'Pantalla Completa',
      download: 'Descargar Fondo',
      close: 'Cerrar'
    },
    tooltips: {
      enterFullscreen: 'Clic para modo pantalla completa',
      exitFullscreen: 'Presiona ESC para salir',
      downloadImage: 'Descargar como imagen PNG'
    },
    headings: {
      resolution: 'Resolución',
      customColor: 'Color Personalizado'
    },
    messages: {
      downloadSuccess: 'Fondo descargado exitosamente',
      downloadError: 'Error al descargar el fondo',
      fullscreenError: 'Error al entrar en pantalla completa',
      fullscreenEnter: 'Modo pantalla completa activado'
    }
  },
  fr: {
    errors: {
      invalidColor: 'Code couleur invalide',
      invalidResolution: 'Résolution invalide',
      downloadFailed: 'Échec du téléchargement du fond',
      fullscreenError: 'Échec du passage en mode plein écran',
      componentError: 'Une erreur de composant est survenue',
      retry: 'Réessayer',
      title: 'Une erreur est survenue',
      description: 'Une erreur est survenue lors du traitement de votre demande',
      tryAgain: 'Réessayer',
      backHome: 'Retour à l\'accueil'
    },
    fullscreen: {
      exitMac: "Appuyez sur ⌘+⌃+F pour quitter",
      exitWindows: "Appuyez sur Échap pour quitter"
    },
    resolution: {
      selectPlaceholder: "Sélectionner la résolution",
      custom: "Personnalisée",
      width: "Largeur",
      height: "Hauteur"
    },
    aria: {
      customColorInput: "Entrer le code hexadécimal de la couleur personnalisée",
      fullscreenButton: "Basculer en mode plein écran",
      downloadButton: "Télécharger l'image de fond"
    },
    buttons: {
      fullscreen: 'Plein Écran',
      download: 'Télécharger Fond',
      close: 'Fermer'
    },
    tooltips: {
      enterFullscreen: 'Cliquez pour le mode plein écran',
      exitFullscreen: 'Appuyez sur Échap pour quitter',
      downloadImage: 'Télécharger en PNG'
    },
    headings: {
      resolution: 'Résolution',
      customColor: 'Couleur Personnalisée'
    },
    messages: {
      downloadSuccess: 'Fond téléchargé avec succès',
      downloadError: 'Échec du téléchargement',
      fullscreenError: 'Échec du mode plein écran',
      fullscreenEnter: 'Mode plein écran activé'
    }
  },
  de: {
    errors: {
      invalidColor: 'Ungültiger Farbcode',
      invalidResolution: 'Ungültige Auflösung',
      downloadFailed: 'Hintergrund konnte nicht heruntergeladen werden',
      fullscreenError: 'Vollbildmodus konnte nicht aktiviert werden',
      componentError: 'Komponentenfehler aufgetreten',
      retry: 'Wiederholen',
      title: 'Etwas ist schief gelaufen',
      description: 'Bei der Verarbeitung Ihrer Anfrage ist ein Fehler aufgetreten',
      tryAgain: 'Erneut versuchen',
      backHome: 'Zurück zur Startseite'
    },
    fullscreen: {
      exitMac: "Drücken Sie ⌘+⌃+F zum Beenden",
      exitWindows: "Drücken Sie Esc zum Beenden"
    },
    resolution: {
      selectPlaceholder: "Auflösung wählen",
      custom: "Benutzerdefiniert",
      width: "Breite",
      height: "Höhe"
    },
    aria: {
      customColorInput: "Benutzerdefinierten Hexadezimal-Farbcode eingeben",
      fullscreenButton: "Vollbildmodus umschalten",
      downloadButton: "Hintergrundbild herunterladen"
    },
    buttons: {
      fullscreen: 'Vollbildmodus',
      download: 'Hintergrund Herunterladen',
      close: 'Schließen'
    },
    tooltips: {
      enterFullscreen: 'Klicken für Vollbildmodus',
      exitFullscreen: 'ESC drücken zum Beenden',
      downloadImage: 'Als PNG herunterladen'
    },
    headings: {
      resolution: 'Auflösung',
      customColor: 'Benutzerdefinierte Farbe'
    },
    messages: {
      downloadSuccess: 'Hintergrund erfolgreich heruntergeladen',
      downloadError: 'Fehler beim Herunterladen',
      fullscreenError: 'Fehler beim Aktivieren des Vollbildmodus',
      fullscreenEnter: 'Vollbildmodus aktiviert'
    }
  },
  it: {
    errors: {
      invalidColor: 'Codice colore non valido',
      invalidResolution: 'Risoluzione non valida',
      downloadFailed: 'Download dello sfondo fallito',
      fullscreenError: 'Impossibile entrare in modalità schermo intero',
      componentError: 'Si è verificato un errore del componente',
      retry: 'Riprova',
      title: 'Qualcosa è andato storto',
      description: 'Si è verificato un errore durante l\'elaborazione della richiesta',
      tryAgain: 'Riprova',
      backHome: 'Torna alla Home'
    },
    fullscreen: {
      exitMac: "Premi ⌘+⌃+F per uscire",
      exitWindows: "Premi Esc per uscire"
    },
    resolution: {
      selectPlaceholder: "Seleziona risoluzione",
      custom: "Personalizzata",
      width: "Larghezza",
      height: "Altezza"
    },
    aria: {
      customColorInput: "Inserisci codice hex del colore personalizzato",
      fullscreenButton: "Attiva/disattiva modalità schermo intero",
      downloadButton: "Scarica immagine di sfondo"
    },
    buttons: {
      fullscreen: 'Schermo Intero',
      download: 'Scarica Sfondo',
      close: 'Chiudi'
    },
    tooltips: {
      enterFullscreen: 'Clicca per modalità schermo intero',
      exitFullscreen: 'Premi ESC per uscire',
      downloadImage: 'Scarica come PNG'
    },
    headings: {
      resolution: 'Risoluzione',
      customColor: 'Colore Personalizzato'
    },
    messages: {
      downloadSuccess: 'Sfondo scaricato con successo',
      downloadError: 'Errore durante il download',
      fullscreenError: 'Errore attivazione schermo intero',
      fullscreenEnter: 'Modalità schermo intero attivata'
    }
  },
  pl: {
    errors: {
      invalidColor: 'Nieprawidłowy kod koloru',
      invalidResolution: 'Nieprawidłowa rozdzielczość',
      downloadFailed: 'Nie udało się pobrać tła',
      fullscreenError: 'Nie udało się przejść w tryb pełnoekranowy',
      componentError: 'Wystąpił błąd komponentu',
      retry: 'Ponów',
      title: 'Coś poszło nie tak',
      description: 'Wystąpił błąd podczas przetwarzania żądania',
      tryAgain: 'Spróbuj ponownie',
      backHome: 'Wróć do strony głównej'
    },
    fullscreen: {
      exitMac: "Naciśnij ⌘+⌃+F aby wyjść",
      exitWindows: "Naciśnij Esc aby wyjść"
    },
    resolution: {
      selectPlaceholder: "Wybierz rozdzielczość",
      custom: "Niestandardowa",
      width: "Szerokość",
      height: "Wysokość"
    },
    aria: {
      customColorInput: "Wprowadź kod hex niestandardowego koloru",
      fullscreenButton: "Przełącz tryb pełnoekranowy",
      downloadButton: "Pobierz obraz tła"
    },
    buttons: {
      fullscreen: 'Pełny Ekran',
      download: 'Pobierz Tło',
      close: 'Zamknij'
    },
    tooltips: {
      enterFullscreen: 'Kliknij dla trybu pełnoekranowego',
      exitFullscreen: 'Naciśnij ESC aby wyjść',
      downloadImage: 'Pobierz jako PNG'
    },
    headings: {
      resolution: 'Rozdzielczość',
      customColor: 'Niestandardowy Kolor'
    },
    messages: {
      downloadSuccess: 'Tło pobrane pomyślnie',
      downloadError: 'Błąd podczas pobierania',
      fullscreenError: 'Błąd trybu pełnoekranowego',
      fullscreenEnter: 'Tryb pełnoekranowy aktywowany'
    }
  },
  pt: {
    errors: {
      invalidColor: 'Código de cor inválido',
      invalidResolution: 'Resolução inválida',
      downloadFailed: 'Falha ao baixar o fundo',
      fullscreenError: 'Falha ao entrar em tela cheia',
      componentError: 'Ocorreu um erro no componente',
      retry: 'Tentar novamente',
      title: 'Algo deu errado',
      description: 'Ocorreu um erro ao processar sua solicitação',
      tryAgain: 'Tentar novamente',
      backHome: 'Voltar para o início'
    },
    fullscreen: {
      exitMac: "Pressione ⌘+⌃+F para sair",
      exitWindows: "Pressione Esc para sair"
    },
    resolution: {
      selectPlaceholder: "Selecione a resolução",
      custom: "Personalizada",
      width: "Largura",
      height: "Altura"
    },
    aria: {
      customColorInput: "Digite o código hex da cor personalizada",
      fullscreenButton: "Alternar modo tela cheia",
      downloadButton: "Baixar imagem de fundo"
    },
    buttons: {
      fullscreen: 'Tela Cheia',
      download: 'Baixar Fundo',
      close: 'Fechar'
    },
    tooltips: {
      enterFullscreen: 'Clique para modo tela cheia',
      exitFullscreen: 'Pressione ESC para sair',
      downloadImage: 'Baixar como PNG'
    },
    headings: {
      resolution: 'Resolução',
      customColor: 'Cor Personalizada'
    },
    messages: {
      downloadSuccess: 'Fundo baixado com sucesso',
      downloadError: 'Erro ao baixar fundo',
      fullscreenError: 'Erro ao entrar em tela cheia',
      fullscreenEnter: 'Modo tela cheia ativado'
    }
  },
  'pt-BR': {
  errors: {
    invalidColor: 'Código de cor inválido',
    invalidResolution: 'Resolução inválida',
    downloadFailed: 'Falha ao baixar o plano de fundo',
    fullscreenError: 'Falha ao entrar em tela cheia',
    componentError: 'Ocorreu um erro no componente',
    retry: 'Tentar novamente',
    title: 'Algo deu errado',
    description: 'Ocorreu um erro ao processar sua solicitação',
    tryAgain: 'Tentar novamente',
    backHome: 'Voltar para o início'
  },
    fullscreen: {
      exitMac: "Pressione ⌘+⌃+F para sair",
      exitWindows: "Pressione Esc para sair"
    },
    resolution: {
      selectPlaceholder: "Selecione a resolução",
      custom: "Personalizada",
      width: "Largura",
      height: "Altura"
    },
    aria: {
      customColorInput: "Digite o código hex da cor personalizada",
      fullscreenButton: "Alternar modo tela cheia",
      downloadButton: "Baixar imagem de fundo"
    },
    buttons: {
      fullscreen: 'Tela Cheia',
      download: 'Baixar Fundo',
      close: 'Fechar'
    },
    tooltips: {
      enterFullscreen: 'Clique para modo tela cheia',
      exitFullscreen: 'Pressione ESC para sair',
      downloadImage: 'Baixar como PNG'
    },
    headings: {
      resolution: 'Resolução',
      customColor: 'Cor Personalizada'
    },
    messages: {
      downloadSuccess: 'Fundo baixado com sucesso',
      downloadError: 'Erro ao baixar fundo',
      fullscreenError: 'Erro ao entrar em tela cheia',
      fullscreenEnter: 'Modo tela cheia ativado'
    }
  },
  nl: {
    errors: {
      invalidColor: 'Ongeldige kleurcode',
      invalidResolution: 'Ongeldige resolutie',
      downloadFailed: 'Downloaden achtergrond mislukt',
      fullscreenError: 'Kan niet naar volledig scherm gaan',
      componentError: 'Er is een componentfout opgetreden',
      retry: 'Opnieuw proberen',
      title: 'Er is iets misgegaan',
      description: 'Er is een fout opgetreden bij het verwerken van uw verzoek',
      tryAgain: 'Opnieuw proberen',
      backHome: 'Terug naar home'
    },
    fullscreen: {
      exitMac: "Druk op ⌘+⌃+F om te sluiten",
      exitWindows: "Druk op Esc om te sluiten"
    },
    resolution: {
      selectPlaceholder: "Selecteer resolutie",
      custom: "Aangepast",
      width: "Breedte",
      height: "Hoogte"
    },
    aria: {
      customColorInput: "Voer aangepaste kleur hexcode in",
      fullscreenButton: "Volledig scherm in-/uitschakelen",
      downloadButton: "Download achtergrondafbeelding"
    },
    buttons: {
      fullscreen: 'Volledig Scherm',
      download: 'Download Achtergrond',
      close: 'Sluiten'
    },
    tooltips: {
      enterFullscreen: 'Klik voor volledig scherm',
      exitFullscreen: 'Druk op ESC om te sluiten',
      downloadImage: 'Download als PNG'
    },
    headings: {
      resolution: 'Resolutie',
      customColor: 'Aangepaste Kleur'
    },
    messages: {
      downloadSuccess: 'Achtergrond succesvol gedownload',
      downloadError: 'Fout bij downloaden',
      fullscreenError: 'Fout bij volledig scherm',
      fullscreenEnter: 'Volledig scherm geactiveerd'
    }
  },
  ja: {
    errors: {
      invalidColor: '無効なカラーコード',
      invalidResolution: '無効な解像度',
      downloadFailed: '背景のダウンロードに失敗しました',
      fullscreenError: 'フルスクリーンモードに移行できませんでした',
      componentError: 'コンポーネントエラーが発生しました',
      retry: '再試行',
      title: 'エラーが発生しました',
      description: 'リクエストの処理中にエラーが発生しました',
      tryAgain: 'もう一度試す',
      backHome: 'ホームに戻る'
    },
    fullscreen: {
      exitMac: "⌘+⌃+F で終了",
      exitWindows: "Escキーで終了"
    },
    resolution: {
      selectPlaceholder: "解像度を選択",
      custom: "カスタム",
      width: "幅",
      height: "高さ"
    },
    aria: {
      customColorInput: "カスタムカラーの16進コードを入力",
      fullscreenButton: "フルスクリーンモードの切り替え",
      downloadButton: "背景画像をダウンロード"
    },
    buttons: {
      fullscreen: 'フルスクリーン',
      download: '背景をダウンロード',
      close: '閉じる'
    },
    tooltips: {
      enterFullscreen: 'クリックでフルスクリーン',
      exitFullscreen: 'ESCキーで終了',
      downloadImage: 'PNGでダウンロード'
    },
    headings: {
      resolution: '解像度',
      customColor: 'カスタムカラー'
    },
    messages: {
      downloadSuccess: '背景のダウンロードが完了しました',
      downloadError: 'ダウンロードに失敗しました',
      fullscreenError: 'フルスクリーンの起動に失敗しました',
      fullscreenEnter: 'フルスクリーンモードを起動しました'
    }
  }
} as const;

export const getContentForLanguage = (lang: SupportedLanguage): CommonTranslations => {
  return contentTranslations[lang] || contentTranslations.en;
};

// Helper function to get specific translations
export const getTranslation = (
  lang: SupportedLanguage,
  section: keyof CommonTranslations,
  key: string
): string => {
  const content = getContentForLanguage(lang);
  const sectionContent = content[section];

  if (sectionContent && typeof sectionContent === 'object') {
    return (sectionContent as Record<string, string>)[key] || '';
  }

  return '';
};